import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * Cutout masks that work on every browser.
 *
 * The catalogue's cutout masks are luminance images: a white silhouette on
 * black, fully opaque, no alpha channel. CSS can use those directly with
 * `mask-mode: luminance` — but Safari on iOS ignores that mode for mask
 * images while still answering `CSS.supports('mask-mode', 'luminance')` with
 * true (measured on iOS 18.7 / Safari 26.6, 21.09.2026, even on a bare
 * element). It then masks by alpha, the mask is opaque everywhere, nothing
 * is cut out, and the product is a filled square of its colour.
 *
 * So the mask is converted once per URL into an alpha mask — what every
 * browser applies the same way — and the mode is never relied on. The
 * stored files stay what they are: the server renderer reads luminance
 * itself and is not affected.
 */

/** Longest edge of the converted mask; the editor shows ~500 CSS px at 3x. */
const MAX_MASK_EDGE = 1600;

/**
 * In place: alpha becomes luminance × alpha, colour becomes white. Exactly
 * what `mask-mode: luminance` computes, so a mask that already is an alpha
 * mask (white shape on a transparent ground) comes out unchanged.
 */
export function luminanceToAlpha(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const a = data[i + 3] ?? 0;
    data[i] = 255;
    data[i + 1] = 255;
    data[i + 2] = 255;
    data[i + 3] = Math.round(
      (0.2126 * r + 0.7152 * g + 0.0722 * b) * (a / 255)
    );
  }
}

/** How long a mask image may take to load before the conversion gives up. */
const LOAD_TIMEOUT_MS = 10_000;

/**
 * Conversions running at once. A catalogue grid mounts dozens of masks in
 * one go; iOS caps the total canvas memory of a page, and past that cap
 * `getContext` answers null — the conversion would fail on exactly the
 * device it exists for.
 */
const MAX_PARALLEL = 2;

/** One conversion per source URL for the lifetime of the page. */
const converted = new Map<string, Promise<string>>();
/**
 * The finished ones, readable without awaiting: a component that mounts with
 * a known mask renders it in its first frame instead of hiding for one.
 */
const resolved = new Map<string, string>();

/** Where the product sits in its mask, as fractions of the image. */
export interface MaskBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}
/** The product's bounding box per source URL, found while converting. */
const bounds = new Map<string, MaskBounds>();

/** Pixels at or below this alpha count as background. */
const BOUNDS_ALPHA_THRESHOLD = 40;

/**
 * The box around the opaque pixels — where the product is. Read from the
 * alpha channel the conversion just produced, so it costs one pass over
 * pixels already in memory.
 */
export function boundsOfAlpha(
  data: Uint8ClampedArray,
  width: number,
  height: number
): MaskBounds | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width * 4 + 3;
    for (let x = 0; x < width; x++) {
      if ((data[row + x * 4] ?? 0) > BOUNDS_ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return {
    x: minX / width,
    y: minY / height,
    width: (maxX - minX + 1) / width,
    height: (maxY - minY + 1) / height,
  };
}

let running = 0;
const waiting: Array<() => void> = [];

function enqueue<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => {
      running += 1;
      job()
        .then(resolve, reject)
        .finally(() => {
          running -= 1;
          waiting.shift()?.();
        });
    };
    if (running < MAX_PARALLEL) run();
    else waiting.push(run);
  });
}

function convert(sourceUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    // A load that never answers would keep every user of this URL hidden for
    // good: the cache below only forgets a conversion that rejects.
    const timeout = setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      image.src = '';
      reject(new Error('mask image timed out'));
    }, LOAD_TIMEOUT_MS);
    // The pixels are read back, so the image must be CORS-clean — the same
    // requirement a CSS mask-image already has.
    image.crossOrigin = 'anonymous';
    image.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('mask image failed to load'));
    };
    image.onload = () => {
      clearTimeout(timeout);
      const canvas = document.createElement('canvas');
      // Hand the backing store back at once instead of waiting for the
      // garbage collector; the canvas budget on iOS is counted per page.
      const release = () => {
        canvas.width = 0;
        canvas.height = 0;
      };
      try {
        if (!image.naturalWidth || !image.naturalHeight) {
          throw new Error('mask image is empty');
        }
        const scale = Math.min(
          1,
          MAX_MASK_EDGE / Math.max(image.naturalWidth, image.naturalHeight)
        );
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no 2d context');
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
        luminanceToAlpha(pixels.data);
        const box = boundsOfAlpha(pixels.data, canvas.width, canvas.height);
        if (box) bounds.set(sourceUrl, box);
        ctx.putImageData(pixels, 0, 0);
        canvas.toBlob((blob) => {
          release();
          if (blob) resolve(URL.createObjectURL(blob));
          else reject(new Error('mask could not be encoded'));
        }, 'image/png');
      } catch (error) {
        release();
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };
    image.src = sourceUrl;
  });
}

export function toAlphaMaskUrl(sourceUrl: string): Promise<string> {
  let pending = converted.get(sourceUrl);
  if (!pending) {
    pending = enqueue(() => convert(sourceUrl));
    converted.set(sourceUrl, pending);
    pending.then(
      (alphaUrl) => resolved.set(sourceUrl, alphaUrl),
      // A failed conversion may be a passing network error: try again on
      // the next request instead of remembering the failure.
      () => converted.delete(sourceUrl)
    );
  }
  return pending;
}

/** The product's box in a converted mask, when the mask is done. */
export function maskBoundsOf(
  sourceUrl: string | null | undefined
): MaskBounds | null {
  return sourceUrl ? (bounds.get(sourceUrl) ?? null) : null;
}

export interface AlphaMask {
  sourceUrl: string;
  /** The converted mask, or null while it is being made. */
  alphaUrl: string | null;
  /** The conversion failed; the luminance mode is the last resort. */
  failed?: boolean;
}

/** What is known about a mask right now, without waiting for anything. */
function knownState(sourceUrl: string): AlphaMask {
  return { sourceUrl, alphaUrl: resolved.get(sourceUrl) ?? null };
}

/** The alpha mask for a cutout mask URL; null when there is no mask. */
export function useAlphaMask(
  sourceUrl: string | null | undefined
): AlphaMask | null {
  const [state, setState] = useState<AlphaMask | null>(() =>
    sourceUrl ? knownState(sourceUrl) : null
  );

  useEffect(() => {
    if (!sourceUrl) {
      setState(null);
      return;
    }
    let live = true;
    const known = knownState(sourceUrl);
    setState((prev) =>
      prev?.sourceUrl === sourceUrl && (prev.alphaUrl || !known.alphaUrl)
        ? prev
        : known
    );
    if (known.alphaUrl) return;
    toAlphaMaskUrl(sourceUrl).then(
      (alphaUrl) => live && setState({ sourceUrl, alphaUrl }),
      () => live && setState({ sourceUrl, alphaUrl: null, failed: true })
    );
    return () => {
      live = false;
    };
  }, [sourceUrl]);

  if (!sourceUrl) return null;
  // The state still belongs to the previous URL for the one render before
  // the effect runs. Answering null there would read as "no mask" and paint
  // the new product as an unmasked square; the old mask would cut the wrong
  // shape. So that render gets what is known about the new URL.
  return state?.sourceUrl === sourceUrl ? state : knownState(sourceUrl);
}

/** Whether the element is still waiting for its mask (it is hidden then). */
export function isMaskPending(mask: AlphaMask | null): boolean {
  return !!mask && !mask.alphaUrl && !mask.failed;
}

const PLACEMENT = {
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
} as const;

/** The CSS for a cutout mask, in whichever state the conversion is. */
export function cutoutMaskStyles(mask: AlphaMask): CSSProperties {
  if (mask.alphaUrl) {
    return {
      WebkitMaskImage: `url(${mask.alphaUrl})`,
      maskImage: `url(${mask.alphaUrl})`,
      maskMode: 'alpha',
      ...PLACEMENT,
    } as CSSProperties;
  }
  if (mask.failed) {
    return {
      WebkitMaskImage: `url(${mask.sourceUrl})`,
      maskImage: `url(${mask.sourceUrl})`,
      maskMode: 'luminance',
      ...PLACEMENT,
    } as CSSProperties;
  }
  // Still converting: unmasked, the element would flash as a full square of
  // the product colour.
  return { opacity: 0 };
}
